import dayjs, { Dayjs } from 'dayjs';
import * as yup from 'yup';
import { dayjsSchema } from './dayjs-schema';
import { educationRegex } from '../utils/regex/educationRegex';
import { Country } from 'country-state-city';
import postalCodes from 'postal-codes-js';

const isOlderThan16Years = (date: dayjs.Dayjs) => {
  return dayjs().diff(date, 'year') >= 16;
};

const today = dayjs();

export const userSchema = yup.object().shape({
  firstName: yup
    .string()
    .required('First Name is required')
    .transform((value: string) => value.replace(/\s+/g, ' ').trim()),
  lastName: yup
    .string()
    .required('Last Name is required')
    .transform((value: string) => value.replace(/\s+/g, ' ').trim()),
  birthdate: dayjsSchema
    .required('Birthdate is required')
    .test('complete-date', 'Please enter a complete Birthdate', (value) => {
      // Ensure that the value is a valid date before performing further checks
      return value && dayjs(value).isValid();
    })
    .test('max-date', 'Birthdate cannot be in the future', (value) => {
      if (!value) return true; // Allow the user to finish typing
      return (value as Dayjs).isBefore(today);
    })
    .test(
      'is-old-enough',
      'User must be at least 16 years old',
      function (value) {
        if (!value) return true;
        return isOlderThan16Years(value as Dayjs);
      }
    ),
  isBirthdateVisible: yup.boolean().required(),
});

const educationSchema = yup.object().shape({
  id: yup.string().optional(),

  universityName: yup
    .string()
    .required('University is required')
    .transform((value: string) => value.replace(/\s+/g, ' ').trim())
    .matches(educationRegex.universityNameRegex, {
      message:
        'University name can only contain letters, numbers, spaces, underscores, hyphens, dots, apostrophes, and question marks.',
    })
    .min(2, 'University name must be at least 2 characters long.')
    .max(80, 'University name must be at most 80 characters long.'),

  idUniversity: yup
    .string()
    .uuid('Invalid University ID format. Must be a valid UUID (v4).')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),

  specialtyName: yup
    .string()
    .required('Specialty is required')
    .transform((value: string) => value.replace(/\s+/g, ' ').trim())
    .matches(educationRegex.specialtyNameRegex, {
      message:
        'Specialty name can only contain letters, numbers, spaces, underscores, hyphens, dots, apostrophes, and question marks.',
    })
    .min(2, 'Specialty name must be at least 2 characters long.')
    .max(80, 'Specialty name must be at most 80 characters long.'),

  idSpecialty: yup
    .string()
    .uuid('Invalid Specialty ID format. Must be a valid UUID (v4).')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),

  startYear: dayjsSchema
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .required('Start education date is required')
    .test(
      'max-date',
      'Start education date cannot be in the future',
      (value) => !value || (value as Dayjs).isBefore(today)
    ),
  endYear: dayjsSchema
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .required('End education date is required')
    .test(
      'max-date',
      'End education date cannot be in the future',
      (value) => !value || (value as Dayjs).isBefore(today)
    )
    .test(
      'is-greater',
      'End education date must be greater than start education date',
      function (value) {
        const { startYear } = this.parent;
        if (!startYear || !value) return true;
        return value > startYear;
      }
    ),
});

export const profileSchema = yup.object().shape({
  firstName: yup
    .string()
    .transform((value: string, originalValue: string) => {
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue.trim() === ''
      ) {
        return null;
      }
      return value.replace(/\s+/g, ' ').trim();
    })
    .optional()
    .nullable()
    .min(2, 'First name must be at least 2 characters'),
  lastName: yup
    .string()
    .transform((value: string, originalValue: string) => {
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue.trim() === ''
      ) {
        return null;
      }
      return value.replace(/\s+/g, ' ').trim();
    })
    .optional()
    .nullable()
    .min(2, 'Last name must be at least 2 characters'),
  birthdate: dayjsSchema
    .optional()
    .nullable()
    .test(
      'is-dayjs',
      'Invalid date format',
      (value) => value === null || dayjs.isDayjs(value as Dayjs)
    )
    .test(
      'max-date',
      'Birthdate cannot be in the future',
      (value) => !value || (value as Dayjs).isBefore(today)
    )
    .test(
      'is-old-enough',
      'User must be at least 16 years old',
      function (value) {
        if (!value) return true;
        return isOlderThan16Years(value as Dayjs);
      }
    ),
  bio: yup
    .string()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .optional()
    .nullable()
    .min(10, 'Biography must be at least 10 characters'),
  country: yup.string().optional().nullable(),
  city: yup.string().optional().nullable(),
  educations: yup
    .array()
    .of(
      yup.lazy((value) => {
        if (Object.keys(value).length === 0) {
          return yup.object().shape({});
        }
        return educationSchema;
      })
    )
    .required(),
});

export const emailsSchema = yup.object().shape({
  emails: yup
    .array()
    .of(
      yup.object().shape({
        value: yup
          .string()
          .email('Invalid email')
          .required('Email is required'),
      })
    )
    .min(1, 'At least one email is required')
    .required('Emails are required')
    .test('unique-emails', 'Emails must be unique', (emails) => {
      const seen = new Set();
      return emails
        ? emails.every((email) => {
            if (seen.has(email.value)) {
              return false;
            }
            seen.add(email.value);
            return true;
          })
        : true;
    }),
});

export const fileSchema = yup.object().shape({
  documents: yup
    .array()
    .of(
      yup.object().shape({
        webkitRelativePath: yup.string().optional(),
        lastModified: yup.number().required(),
        lastModifiedDate: yup.date().required(),
        name: yup.string().required(),
        size: yup
          .number()
          .test('file-size', 'File size exceeds the limit', function (value) {
            const fileType = this.parent.type;
            if (!value || !fileType) return true;

            if (fileType === 'application/pdf') {
              return value <= 25 * 1024 * 1024; // 25MB for PDFs
            }
            if (fileType.startsWith('image/')) {
              return value <= 10 * 1024 * 1024; // 10MB for images
            }
            return true;
          })
          .required(),
        type: yup
          .string()
          .oneOf(
            [
              'image/jpeg',
              'image/png',
              'image/jpg',
              'image/webp',
              'application/pdf',
            ],
            'Unsupported file format'
          )
          .required(),
      })
    )
    .min(1, 'At least one file is required')
    .required('Files are required'),
});

export const userCompanySchema = yup.object().shape({
  companyName: yup
    .string()
    .transform((value: string, originalValue: string) => {
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue.trim() === ''
      ) {
        return null;
      }
      return value.replace(/\s+/g, ' ').trim();
    })
    .optional()
    .nullable()
    .min(2, 'Company name must be at least 2 characters'),
  industryIds: yup.array().of(yup.string().required()).optional().nullable(),
  country: yup
    .string()
    .transform((value: string, originalValue: string) => {
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue.trim() === ''
      ) {
        return null;
      }
      return value.replace(/\s+/g, ' ').trim();
    })
    .optional()
    .nullable(),
  city: yup
    .string()
    .transform((value: string, originalValue: string) => {
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue.trim() === ''
      ) {
        return null;
      }
      return value.replace(/\s+/g, ' ').trim();
    })
    .optional()
    .nullable(),
  address: yup
    .string()
    .transform((value: string, originalValue: string) => {
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue.trim() === ''
      ) {
        return null;
      }
      return value.replace(/\s+/g, ' ').trim();
    })
    .optional()
    .nullable(),
  state: yup
    .string()
    .transform((value: string, originalValue: string) => {
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue.trim() === ''
      ) {
        return null;
      }
      return value.replace(/\s+/g, ' ').trim();
    })
    .optional()
    .nullable(),
  zipCode: yup
    .string()
    .required('ZIP code is required')
    .test(
      'valid-postal-code',
      'Invalid postal code format for selected country',
      function (value) {
        const country = this.parent.country;
        if (!country || !value) return true;

        // Get country code from country name
        const countries = Country.getAllCountries();
        const countryData = countries.find(
          (c: { name: string }) => c.name === country
        );
        if (!countryData) return true;

        const countryCode = countryData.isoCode;
        const result = postalCodes.validate(countryCode, value);
        return result === true;
      }
    ),
  phone: yup
    .string()
    .transform((value: string, originalValue: string) => {
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue.trim() === ''
      ) {
        return null;
      }
      return value.replace(/\s+/g, ' ').trim();
    })
    .optional()
    .nullable(),
  contactEmail: yup
    .string()
    .transform((value: string, originalValue: string) => {
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue.trim() === ''
      ) {
        return null;
      }
      return value.replace(/\s+/g, ' ').trim();
    })
    .optional()
    .nullable(),
  website: yup
    .string()
    .transform((value: string, originalValue: string) => {
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue.trim() === ''
      ) {
        return null;
      }
      return value.replace(/\s+/g, ' ').trim();
    })
    .optional()
    .nullable(),
});

// Live Agent
export const liveAgentProfileSettingsSchema = yup.object().shape({
  firstName: yup
    .string()
    .transform((value: string, originalValue: string) => {
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue.trim() === ''
      ) {
        return null;
      }
      return value.replace(/\s+/g, ' ').trim();
    })
    .optional()
    .nullable()
    .min(2, 'First name must be at least 2 characters'),
  lastName: yup
    .string()
    .transform((value: string, originalValue: string) => {
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue.trim() === ''
      ) {
        return null;
      }
      return value.replace(/\s+/g, ' ').trim();
    })
    .optional()
    .nullable()
    .min(2, 'Last name must be at least 2 characters'),
  bio: yup
    .string()
    .transform((value: string, originalValue: string) => {
      if (
        originalValue === undefined ||
        originalValue === null ||
        originalValue.trim() === ''
      ) {
        return null;
      }
      return value.replace(/\s+/g, ' ').trim();
    })
    .optional()
    .nullable()
    .min(10, 'Biography must be at least 10 characters'),
});
